/**
 * MiraGlow Dynamic Image, Video & Product Rating/Review Detection
 *
 * Includes ONLY:
 * 1. Dynamic ImageObject detection
 * 2. Dynamic VideoObject detection
 * 3. Product rating/review detection
 */

class MiraGlowMediaSchemaGenerator {
  constructor() {
    this.siteUrl = 'https://miraglow.ca';
    this.brandName = 'MiraGlow';
    this.schemaContainer = [];
  }

  /**
   * Initialize on page load
   */
  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.generateSchemas();
      });
    } else {
      this.generateSchemas();
    }
  }

  /**
   * Generate only ImageObject and VideoObject schemas
   * and detect product rating/review data.
   */
  generateSchemas() {
    this.schemaContainer = [];

    // 1. Dynamic ImageObject detection
    this.generateImageSchemas();

    // 2. Dynamic VideoObject detection
    this.generateVideoSchemas();

    // 3. Product rating/review detection
    this.detectProductRatingAndReviews();

    // Inject generated schemas
    this.injectSchemas();
  }

  /**
   * =========================================================
   * 1. DYNAMIC IMAGEOBJECT DETECTION
   * =========================================================
   */
  generateImageSchemas() {
    const images = this.detectAllImages();

    images.forEach((img, index) => {
      const imageSchema = {
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        url: img.src,
        name: img.alt || `${this.brandName} Image ${index + 1}`,
        description:
          img.alt ||
          img.title ||
          `${this.brandName} image`
      };

      this.schemaContainer.push(imageSchema);
    });
  }

  /**
   * Detect all images on the page
   */
  detectAllImages() {
    const images = [];

    // Detect <img> elements
    document.querySelectorAll('img').forEach((img) => {
      const src = img.currentSrc || img.src;

      if (
        src &&
        !src.includes('icon') &&
        !src.includes('logo') &&
        !src.startsWith('data:')
      ) {
        images.push({
          src: this.makeAbsoluteUrl(src),
          alt: img.alt || '',
          title: img.title || ''
        });
      }
    });

    // Detect CSS background images
    document
      .querySelectorAll('[style*="background-image"]')
      .forEach((element) => {
        const backgroundImage = element.style.backgroundImage;

        const match = backgroundImage.match(
          /url\(['"]?(.+?)['"]?\)/
        );

        if (match && match[1]) {
          images.push({
            src: this.makeAbsoluteUrl(match[1]),
            alt: element.getAttribute('aria-label') || '',
            title: element.getAttribute('title') || ''
          });
        }
      });

    // Remove duplicate images
    return [
      ...new Map(
        images.map((image) => [image.src, image])
      ).values()
    ];
  }

  /**
   * =========================================================
   * 2. DYNAMIC VIDEOOBJECT DETECTION
   * =========================================================
   */
  generateVideoSchemas() {
    const videos = this.detectAllVideos();

    videos.forEach((video) => {
      const videoSchema = {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: video.title,
        description: video.description,
        contentUrl: video.url,
        uploadDate: video.uploadDate
      };

      // Add thumbnail only when available
      if (video.thumbnail) {
        videoSchema.thumbnailUrl = video.thumbnail;
      }

      // Add duration only when available
      if (video.duration) {
        videoSchema.duration = video.duration;
      }

      this.schemaContainer.push(videoSchema);
    });
  }

  /**
   * Detect all supported videos
   */
  detectAllVideos() {
    const videos = [];

    // Detect HTML5 <video> elements
    document.querySelectorAll('video').forEach((video, index) => {
      const source =
        video.querySelector('source')?.src ||
        video.currentSrc ||
        video.src;

      if (!source) {
        return;
      }

      videos.push({
        url: this.makeAbsoluteUrl(source),
        title:
          video.getAttribute('title') ||
          `${this.brandName} Video ${index + 1}`,
        description:
          video.getAttribute('data-description') ||
          `${this.brandName} video`,
        thumbnail: video.poster
          ? this.makeAbsoluteUrl(video.poster)
          : '',
      duration: this.formatDuration(video.duration),
uploadDate: '2026-09-17T00:00:00+05:30'
});
    });

    // Detect YouTube and Vimeo iframe videos
    document
      .querySelectorAll(
        'iframe[src*="youtube.com"], iframe[src*="youtu.be"], iframe[src*="vimeo.com"]'
      )
      .forEach((iframe, index) => {
        const videoUrl = iframe.src;

        videos.push({
          url: videoUrl,
          title:
            iframe.getAttribute('title') ||
            `${this.brandName} Video ${index + 1}`,
          description:
            iframe.getAttribute('data-description') ||
            `${this.brandName} embedded video`,
          thumbnail: this.getVideoThumbnail(videoUrl),
          duration: ''
        });
      });

    // Detect videos using data-video-url
    document
      .querySelectorAll('[data-video-url]')
      .forEach((element, index) => {
        const videoUrl =
          element.getAttribute('data-video-url');

        if (!videoUrl) {
          return;
        }

        videos.push({
          url: this.makeAbsoluteUrl(videoUrl),
          title:
            element.getAttribute('data-video-title') ||
            `${this.brandName} Video ${index + 1}`,
          description:
            element.getAttribute('data-video-description') ||
            `${this.brandName} video`,
          thumbnail:
            element.getAttribute('data-video-thumbnail')
              ? this.makeAbsoluteUrl(
                  element.getAttribute('data-video-thumbnail')
                )
              : '',
          duration:
            element.getAttribute('data-video-duration') || ''
        });
      });

    // Remove duplicate videos
    return [
      ...new Map(
        videos.map((video) => [video.url, video])
      ).values()
    ];
  }

  /**
   * Generate YouTube thumbnail
   */
  getVideoThumbnail(videoUrl) {
    if (
      videoUrl.includes('youtube.com') ||
      videoUrl.includes('youtu.be')
    ) {
      const match = videoUrl.match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([^&?/"']+)/
      );

      if (match && match[1]) {
        return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
      }
    }

    return '';
  }

  /**
   * =========================================================
   * 3. PRODUCT RATING / REVIEW DETECTION
   * =========================================================
   *
   * This detects existing rating and review information
   * from the page.
   *
   * It does NOT create fake/default ratings.
   */
  detectProductRatingAndReviews() {
    const ratingElement = document.querySelector(
      '[data-rating], ' +
      '[data-average-rating], ' +
      '[class*="rating"], ' +
      '[class*="Rating"], ' +
      '[aria-label*="rating"], ' +
      '[aria-label*="Rating"]'
    );

    const reviewElement = document.querySelector(
      '[data-review-count], ' +
      '[data-reviews-count], ' +
      '[class*="review-count"], ' +
      '[class*="Review-count"], ' +
      '[class*="reviews-count"], ' +
      '[class*="Reviews-count"]'
    );

    const ratingText =
      ratingElement?.textContent ||
      ratingElement?.getAttribute('data-rating') ||
      '';

    const reviewText =
      reviewElement?.textContent ||
      reviewElement?.getAttribute('data-review-count') ||
      '';

    const ratingMatch = ratingText.match(
      /(?:^|\s)([0-5](?:\.\d+)?)(?:\s*(?:\/\s*5|out of 5|stars?|★))?/i
    );

    const reviewMatch =
      reviewText.match(/\d[\d,]*/);

    const ratingValue = ratingMatch
      ? parseFloat(ratingMatch[1])
      : null;

    const reviewCount = reviewMatch
      ? parseInt(
          reviewMatch[0].replace(/,/g, ''),
          10
        )
      : null;

    if (
      ratingValue !== null ||
      reviewCount !== null
    ) {
      window.MiraGlowProductRating = {
        ratingValue: ratingValue,
        reviewCount: reviewCount
      };

      console.log(
        '⭐ MiraGlow Product Rating/Review Data:',
        window.MiraGlowProductRating
      );
    }
  }

  /**
   * =========================================================
   * URL HELPER
   * =========================================================
   */
  makeAbsoluteUrl(url) {
    if (!url) {
      return '';
    }

    if (
      url.startsWith('http://') ||
      url.startsWith('https://')
    ) {
      return url;
    }

    if (url.startsWith('//')) {
      return 'https:' + url;
    }

    if (url.startsWith('/')) {
      return this.siteUrl + url;
    }

    return this.siteUrl + '/' + url;
  }

  /**
   * =========================================================
   * VIDEO DURATION
   * =========================================================
   */
  formatDuration(seconds) {
    if (
      !seconds ||
      !isFinite(seconds)
    ) {
      return '';
    }

    const hours = Math.floor(
      seconds / 3600
    );

    const minutes = Math.floor(
      (seconds % 3600) / 60
    );

    const secs = Math.floor(
      seconds % 60
    );

    let duration = 'PT';

    if (hours > 0) {
      duration += hours + 'H';
    }

    if (minutes > 0) {
      duration += minutes + 'M';
    }

    if (secs > 0) {
      duration += secs + 'S';
    }

    return duration === 'PT'
      ? ''
      : duration;
  }

  /**
   * =========================================================
   * INJECT JSON-LD
   * =========================================================
   */
  injectSchemas() {
    // Prevent duplicate injection
    if (
      document.querySelector(
        'script[type="application/ld+json"][data-miraglow-media-schema]'
      )
    ) {
      return;
    }

    this.schemaContainer.forEach((schema) => {
      const script =
        document.createElement('script');

      script.type =
        'application/ld+json';

      script.setAttribute(
        'data-miraglow-media-schema',
        'true'
      );

      script.textContent =
        JSON.stringify(
          schema,
          null,
          2
        );

      document.head.appendChild(script);
    });

    console.log(
      `✅ MiraGlow Dynamic Media Schema: ${this.schemaContainer.length} schemas injected`
    );
  }

  /**
   * Debugging
   */
  getSchemas() {
    return this.schemaContainer;
  }
}

/**
 * Initialize
 */
const miraglowMediaSchema =
  new MiraGlowMediaSchemaGenerator();

miraglowMediaSchema.init();

/**
 * Make available globally for debugging
 */
window.MiraGlowMediaSchema =
  miraglowMediaSchema;